import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api';

export interface User {
  id: string | number;
  username?: string;
  email?: string;
  fullName?: string;
  phone?: string;
  address?: string;
  city?: string;
  governorate?: string;
  branchId?: number;
  delegateId?: number;
  tenantId?: string;
  tenantRole?: string;
  isActive?: boolean;
  role: string;
  roleName?: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: User, token: string) => void;
  updateUser: (userData: User) => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const USER_KEY = 'user';
const TOKEN_KEY = 'token';

const normalize = (value?: string | null) =>
  String(value ?? '').trim().toLowerCase();

type TenantClaims = {
  tenantId?: string;
  tenantRole?: string;
};

const readTenantClaims = (token: string): TenantClaims => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return {};

    const normalizedPayload = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '=',
    );
    const claims = JSON.parse(atob(paddedPayload)) as Record<string, unknown>;

    return {
      tenantId:
        typeof claims.TenantId === 'string'
          ? claims.TenantId
          : typeof claims.tenantId === 'string'
            ? claims.tenantId
            : undefined,
      tenantRole:
        typeof claims.tenant_role === 'string'
          ? claims.tenant_role
          : typeof claims.tenantRole === 'string'
            ? claims.tenantRole
            : undefined,
    };
  } catch {
    return {};
  }
};

const normalizeUser = (userData: User, token?: string): User => {
  const tokenClaims = token ? readTenantClaims(token) : {};

  return {
    ...userData,
    tenantId: userData.tenantId ?? tokenClaims.tenantId,
    tenantRole: userData.tenantRole ?? tokenClaims.tenantRole,
    role: normalize(userData.role),
    permissions: Array.isArray(userData.permissions)
      ? userData.permissions
      : [],
  };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get('/api/auth/me');
        if (!response.data?.success || !response.data?.data) {
          throw new Error('Invalid /me response');
        }

        const data = response.data.data;
        const userData = normalizeUser(
          {
            id: data.id,
            username: data.username,
            email: data.email,
            fullName: data.fullName,
            phone: data.phone,
            address: data.address,
            city: data.city,
            governorate: data.governorate,
            branchId: data.branchId,
            delegateId: data.delegateId,
            tenantId: data.tenantId,
            tenantRole: data.tenantRole,
            isActive: data.isActive,
            role: data.role,
            roleName: data.roleName,
            permissions: Array.isArray(data.permissions)
              ? data.permissions
              : [],
          },
          token,
        );

        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Session restore failed:', error);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  const login = (userData: User, token: string) => {
    const normalizedUser = normalizeUser(userData, token);
    localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
    localStorage.setItem(TOKEN_KEY, token);
    setUser(normalizedUser);
    setIsAuthenticated(true);
  };

  const updateUser = (userData: User) => {
    const normalizedUser = normalizeUser(userData);
    localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
    setUser(normalizedUser);
  };

  const logout = () => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    const wanted = normalize(role);
    return (
      normalize(user.role) === wanted ||
      normalize(user.roleName) === wanted ||
      normalize(user.tenantRole) === wanted
    );
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (normalize(user.role) === 'admin') {
      return true;
    }
    return user.permissions?.includes(permission) ?? false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        updateUser,
        logout,
        hasRole,
        hasAnyRole,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
